import { BaseApiService } from '../../common/services/baseApiService';
import { API_URL } from '../../common/utils/util';
import {
  Chain,
  DashboardFiltros,
  NegadoDetalleRow,
  NegadoProductoRow,
  NegadoTallaRow,
  MetaResponse,
  SucursalRow,
  TiendaResponse,
  VendedorDetalleResponse,
  VendedorProductoDetalleResponse,
} from '../types/indicadores.types';
import type { EstadoScorecard, RankingScorecard, ScorecardTienda } from '../types/scorecard.types';

/**
 * Arma la query string para los endpoints de rango (`/dashboard/sucursales`,
 * `/dashboard/negados-productos`, `/dashboard/negados-tallas`). `chain`/
 * `branchOffice` se OMITEN cuando están vacíos (el backend los trata como
 * "sin filtro", no como filtrar por cadena/sucursal vacía).
 */
function buildRangeQuery(filtros: DashboardFiltros): string {
  const params = new URLSearchParams();
  params.set('desde', filtros.desde);
  params.set('hasta', filtros.hasta);

  if (filtros.chain) {
    params.set('chain', filtros.chain);
  }

  if (filtros.branchOffice !== undefined && filtros.branchOffice !== null) {
    params.set('branchOffice', String(filtros.branchOffice));
  }

  // Estatus de PRODUCTO (PT5): un parámetro `status` POR CADA valor
  // (`append`, no `set`) — el backend ya acepta `?status=A&status=B` en
  // `/dashboard/sucursales`/`negados-*`. Vacío/`undefined` se OMITE (sin
  // filtro = Todos, comportamiento default).
  if (filtros.status?.length) {
    for (const s of filtros.status) {
      params.append('status', s);
    }
  }

  return params.toString();
}

export interface TiendaCatalogoApi {
  chain: string;
  num: number;
  tienda: string;
  supervisor: string | null;
  regional: string | null;
}

/** Una tienda CERRADA del registro de la API (SPEC 2026-10-13, punto b). */
export interface TiendaCerradaApi extends TiendaCatalogoApi {
  ultimaActividad: string;
  cerrada: string;
}

/** Lo que `GET /dashboard/meta/estado` le dice al tablero (ver `getPresupuestoEstado`). */
export interface EstadoPresupuesto {
  /** La API tiene el backend de Presupuesto (la ruta respondió 200). */
  disponible: boolean;
  /** Y su configuración pide mostrar la pestaña (`config.dashboard.presupuesto.visible`). */
  pestanaVisible: boolean;
}

export interface CatalogoTiendasApi {
  tiendas: TiendaCatalogoApi[];
  total: number;
  sinCatalogar: string[];
  retiradas: string[];
  /** Ausente en una API anterior al 13-oct. */
  cerradas?: TiendaCerradaApi[];
}

/**
 * Texto de error de una respuesta fallida del tablero.
 *
 * La API valida el rango antes de consultar y responde 400 con `message` en
 * español — `string` o `string[]` (el formato de NestJS cuando falla más de una
 * regla). `BaseApiService` hacía `new Error(message)`, y un arreglo se volvía
 * «a,b» pegado. Aquí se unen con « · ». Un 500 sin mensaje propio se dice en
 * español: «Internal server error» delante de una tienda no explica nada.
 */
export function mensajeDeError(cuerpo: unknown, status: number): string {
  const msg = (cuerpo as { message?: unknown } | null)?.message;
  if (Array.isArray(msg)) {
    const partes = msg.filter((m): m is string => typeof m === 'string' && m.trim() !== '');
    if (partes.length) return partes.join(' · ');
  } else if (typeof msg === 'string' && msg.trim() !== '' && msg !== 'Internal server error') {
    return msg;
  }
  if (status >= 500) return `El servidor no pudo responder (${status}). Reintenta en un momento.`;
  return `La consulta falló (${status}).`;
}

/**
 * Error de una consulta del tablero, con el `status` HTTP. La pantalla lo usa
 * para distinguir un 400 de validación —«Revisa el periodo», sin «Reintentar»:
 * reintentar no lo arregla— de una falla del servidor, que sí se reintenta.
 */
export interface ErrorConStatus extends Error {
  status: number;
}

/** `status` HTTP de un error del tablero, o `null` si no vino de una respuesta. */
export function statusDeError(err: unknown): number | null {
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : null;
}

export class IndicadoresService extends BaseApiService {
  /**
   * Igual que el de `BaseApiService` (incluido el cierre de sesión ante un
   * 401), pero con el mensaje de `mensajeDeError` y el `status` en el error.
   */
  protected static async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      this.handleAuthError(response);
      const cuerpo = await response.json().catch(() => ({}));
      // Se agrega la propiedad a un `Error` normal en vez de heredar de `Error`:
      // con el transpilado a ES5 una subclase de `Error` pierde `instanceof`.
      const error = new Error(mensajeDeError(cuerpo, response.status)) as ErrorConStatus;
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  /**
   * Catálogo de tiendas ACTIVAS servido por el backend.
   *
   * Antes esta lista venía de `data/stores.ts`, horneada en el bundle: el panel
   * decía "159 tiendas" mientras el ERP tenía 157, porque dos tiendas retiradas
   * meses atrás seguían en el archivo — y una de ellas mantenía viva en los
   * filtros a una supervisora que ya no colabora.
   *
   * Ahora Proscai manda sobre qué tiendas existen. Si el ERP retira una, se cae
   * del tablero sola.
   */
  static async getCatalogoTiendas(): Promise<CatalogoTiendasApi> {
    try {
      const response = await fetch(`${API_URL}/dashboard/catalogo-tiendas`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      return await this.handleResponse<CatalogoTiendasApi>(response);
    } catch (error) {
      console.error('Get catalogo tiendas error:', error);
      throw error;
    }
  }

  static async getSucursales(
    filtros: DashboardFiltros,
  ): Promise<SucursalRow[]> {
    try {
      const query = buildRangeQuery(filtros);
      const response = await fetch(`${API_URL}/dashboard/sucursales?${query}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      return await this.handleResponse<SucursalRow[]>(response);
    } catch (error) {
      console.error('Get dashboard sucursales error:', error);
      throw error;
    }
  }

  static async getNegadosProductos(
    filtros: DashboardFiltros,
  ): Promise<NegadoProductoRow[]> {
    try {
      const query = buildRangeQuery(filtros);
      const response = await fetch(
        `${API_URL}/dashboard/negados-productos?${query}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        },
      );

      return await this.handleResponse<NegadoProductoRow[]>(response);
    } catch (error) {
      console.error('Get dashboard negados-productos error:', error);
      throw error;
    }
  }

  static async getNegadosTallas(
    filtros: DashboardFiltros,
  ): Promise<NegadoTallaRow[]> {
    try {
      const query = buildRangeQuery(filtros);
      const response = await fetch(
        `${API_URL}/dashboard/negados-tallas?${query}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        },
      );

      return await this.handleResponse<NegadoTallaRow[]>(response);
    } catch (error) {
      console.error('Get dashboard negados-tallas error:', error);
      throw error;
    }
  }

  /**
   * `GET /dashboard/negados-detalle` (PT7): ocurrencias día/hora/sucursal/
   * talla de negado de UN producto puntual — el drill "Detalle Negado" que se
   * abre al hacer clic en una fila de "Productos más negados". Reusa
   * `buildRangeQuery` para que el drill respete el MISMO rango/estatus/alcance
   * de cadena-sucursal que ya está activo en la vista Global, y le agrega
   * `productId` a la query.
   */
  static async getNegadosDetalle(
    productId: string,
    filtros: DashboardFiltros,
  ): Promise<NegadoDetalleRow[]> {
    try {
      const params = new URLSearchParams(buildRangeQuery(filtros));
      params.set('productId', productId);

      const response = await fetch(
        `${API_URL}/dashboard/negados-detalle?${params.toString()}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        },
      );

      return await this.handleResponse<NegadoDetalleRow[]>(response);
    } catch (error) {
      console.error('Get dashboard negados-detalle error:', error);
      throw error;
    }
  }

  /**
   * `GET /dashboard/tienda` (PT8): `desde`/`hasta` son OPCIONALES — el
   * backend (PT3) ya acepta ambos juntos para pedir un rango explícito
   * (ese periodo vs el periodo-anterior de igual longitud); sin ellos sigue
   * siendo el modo vivo de siempre (hoy vs ayer, `rango: null` en la
   * respuesta). Solo se agregan a la query cuando VIENEN LOS DOS juntos —
   * uno solo dejaría un rango a medias que el backend no sabe interpretar.
   */
  static async getTienda(
    chain: Chain,
    branchOffice: number,
    desde?: string,
    hasta?: string,
    vendedorId?: string[],
  ): Promise<TiendaResponse> {
    try {
      const params = new URLSearchParams();
      params.set('chain', chain);
      params.set('branchOffice', String(branchOffice));

      if (desde && hasta) {
        params.set('desde', desde);
        params.set('hasta', hasta);
      }

      // Ítem 11 (2026-07-28): filtro de colaborador — sólo viaja si hay alguno
      // elegido (sin él, el endpoint responde la tienda completa).
      // MULTISELECT (2026-08-03, punto 3): se REPITE el parámetro
      // (`?vendedorId=A&vendedorId=B`), que es lo que espera el DTO del backend
      // (mismo patrón que `status`). Con uno solo, la URL queda igual que antes.
      for (const id of vendedorId ?? []) {
        if (id) params.append('vendedorId', id);
      }

      const response = await fetch(
        `${API_URL}/dashboard/tienda?${params.toString()}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        },
      );

      return await this.handleResponse<TiendaResponse>(response);
    } catch (error) {
      console.error('Get dashboard tienda error:', error);
      throw error;
    }
  }

  /**
   * `GET /dashboard/vendedor-detalle` (Task FE-B/FE-C): trazabilidad por
   * producto de UN vendedor — se abre al hacer clic en una card de
   * `Vendedores.tsx` (Task FE-A conecta `onSelectVendedor`). Espejo del app,
   * que nunca manda `chain`/`branchOffice` (el server los fuerza desde la
   * sesión del `branch_office`); el admin SÍ los manda, igual que el drill de
   * negados (`getNegadosDetalle`), porque ve una tienda ARBITRARIA. Aplica la
   * misma regla de `buildRangeQuery` — omitir cada campo vacío de la query
   * string — pero `desde`/`hasta` son OPCIONALES aquí (el server cae a HOY si
   * faltan, como `/dashboard/tienda`), a diferencia de los endpoints de rango
   * donde son obligatorios.
   */
  static async getVendedorDetalle(
    vendedorId: string,
    filtros: { desde?: string; hasta?: string; chain?: Chain; branchOffice?: number },
  ): Promise<VendedorDetalleResponse> {
    try {
      const params = new URLSearchParams();
      params.set('vendedorId', vendedorId);

      if (filtros.desde) {
        params.set('desde', filtros.desde);
      }
      if (filtros.hasta) {
        params.set('hasta', filtros.hasta);
      }
      if (filtros.chain) {
        params.set('chain', filtros.chain);
      }
      if (filtros.branchOffice !== undefined && filtros.branchOffice !== null) {
        params.set('branchOffice', String(filtros.branchOffice));
      }

      const response = await fetch(
        `${API_URL}/dashboard/vendedor-detalle?${params.toString()}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        },
      );

      return await this.handleResponse<VendedorDetalleResponse>(response);
    } catch (error) {
      console.error('Get dashboard vendedor-detalle error:', error);
      throw error;
    }
  }

  /**
   * `GET /dashboard/vendedor-producto-detalle` (Task FE-A/Fase D, plan
   * `2026-07-22-detalle-por-modelo-y-export-xlsx-plan`): corrida por talla de
   * UN producto (`icod`) para UN vendedor — se abre al hacer clic en una fila
   * de `VendedorDetalleModal` (modal ANIDADO `ModeloDetalleModal`). Espejo del
   * app, que nunca manda `chain`/`branchOffice` (el server los fuerza desde la
   * sesión del `branch_office`); el admin SÍ los manda, igual que
   * `getVendedorDetalle`, porque ve una tienda ARBITRARIA. Mismo criterio de
   * `getVendedorDetalle`: `desde`/`hasta`/`chain`/`branchOffice` son OPCIONALES
   * (el server cae a HOY si faltan desde/hasta).
   */
  static async getVendedorProductoDetalle(
    vendedorId: string,
    icod: string,
    filtros: { desde?: string; hasta?: string; chain?: Chain; branchOffice?: number },
  ): Promise<VendedorProductoDetalleResponse> {
    try {
      const params = new URLSearchParams();
      params.set('vendedorId', vendedorId);
      params.set('icod', icod);

      if (filtros.desde) {
        params.set('desde', filtros.desde);
      }
      if (filtros.hasta) {
        params.set('hasta', filtros.hasta);
      }
      if (filtros.chain) {
        params.set('chain', filtros.chain);
      }
      if (filtros.branchOffice !== undefined && filtros.branchOffice !== null) {
        params.set('branchOffice', String(filtros.branchOffice));
      }

      const response = await fetch(
        `${API_URL}/dashboard/vendedor-producto-detalle?${params.toString()}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        },
      );

      return await this.handleResponse<VendedorProductoDetalleResponse>(response);
    } catch (error) {
      console.error('Get dashboard vendedor-producto-detalle error:', error);
      throw error;
    }
  }
  /**
   * Venta contra meta del tablero de Presupuesto.
   *
   * NO manda `status`: el estatus de producto es un filtro del embudo y no
   * tiene sentido sobre la venta contra el presupuesto.
   */
  static async getMeta(filtros: DashboardFiltros): Promise<MetaResponse> {
    try {
      const params = new URLSearchParams();
      params.set('desde', filtros.desde);
      params.set('hasta', filtros.hasta);
      if (filtros.chain) {
        params.set('chain', filtros.chain);
      }
      if (filtros.branchOffice !== undefined && filtros.branchOffice !== null) {
        params.set('branchOffice', String(filtros.branchOffice));
      }

      const response = await fetch(`${API_URL}/dashboard/meta?${params.toString()}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      return await this.handleResponse<MetaResponse>(response);
    } catch (error) {
      console.error('Get dashboard meta error:', error);
      throw error;
    }
  }

  /**
   * ¿De cuándo es la venta del día que se está mostrando?
   *
   * Devuelve `null` cuando no hay materializado —la venta salió en vivo del
   * ERP— o cuando la API no conoce el endpoint todavía. Nunca lanza: es un
   * adorno informativo y no puede tumbar la pantalla.
   *
   * Existe porque el 2026-09-03 la venta del día se quedó congelada 7 horas
   * mostrando el 26 % de lo real y **nadie podía verlo**. Mi tienda ya decía
   * "actualizado HH:MM"; Global no, y por eso el fallo apareció ahí.
   */
  static async getVentaHoyEstado(): Promise<{
    fecha: string;
    generadoEn: string;
    minutos: number;
    tiendas: number;
    fresco: boolean;
    completo: boolean;
  } | null> {
    try {
      const response = await fetch(`${API_URL}/dashboard/venta-hoy/estado`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * ¿Esta API sabe de Presupuesto, y se pinta su pestaña?
   *
   * El módulo de metas vive en la API desde `1a8372b`, y los entornos no van al
   * mismo paso: el 2026-08-31 desarrollo ya lo servía y producción todavía no
   * (`GET /dashboard/meta/estado` -> 200 contra 404). Con la pestaña visible y
   * el endpoint ausente, quien la pulsara vería una pantalla rota.
   *
   * Antes esto era una constante `PRESUPUESTO_VISIBLE` que había que acordarse
   * de encender. Se pregunta a la API porque **el bundle es el mismo para los
   * dos entornos**, y porque así la pestaña aparece sola el día que la API
   * llegue a producción, sin que nadie tenga que recordar nada.
   *
   * `meta/estado` es la ruta barata del módulo (un conteo de días, sin tocar el
   * ERP), justo lo que hace falta para preguntar «¿existes?».
   *
   * Dos respuestas distintas (28-oct-2026):
   * - `disponible`: la ruta respondió 200. Deja entrar por
   *   `/indicadores?vista=presupuesto`.
   * - `pestanaVisible`: la API, además, dice que la pestaña se muestra. Sale de
   *   `config.dashboard.presupuesto.visible` en la configuración de ESE entorno
   *   (reemplaza a la constante `PESTANA_PRESUPUESTO_LANZADA`, que no podía
   *   encender staging sin encender producción). Sólo el booleano `true` la
   *   enciende; una API anterior, que no manda el campo, la deja oculta.
   *
   * Nunca lanza: si la respuesta no es 200 —404, red caída, sesión vencida— ni la
   * vista ni la pestaña. Ocultarla de más es un fastidio; mostrarla de menos es
   * una pantalla rota delante de dirección.
   */
  static async getPresupuestoEstado(): Promise<EstadoPresupuesto> {
    try {
      const response = await fetch(`${API_URL}/dashboard/meta/estado`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) return { disponible: false, pestanaVisible: false };
      let cuerpo: unknown = null;
      try {
        cuerpo = await response.json();
      } catch {
        cuerpo = null;
      }
      const pestanaVisible =
        typeof cuerpo === 'object' &&
        cuerpo !== null &&
        (cuerpo as { pestanaVisible?: unknown }).pestanaVisible === true;
      return { disponible: true, pestanaVisible };
    } catch {
      return { disponible: false, pestanaVisible: false };
    }
  }

  /**
   * CAPACIDAD del scorecard de tienda (spec 2026-10-16): qué semanas hay, si la
   * pestaña se muestra (`config.scorecard.parametros.visible`, en la API) y qué
   * tiendas ve la cuenta. NUNCA lanza: `null` con 404 (API sin scorecard), 403
   * (rol sin acceso) o falla de red — y la pestaña simplemente no aparece. Es la
   * lección del interruptor compilado que apagó los dos entornos: el permiso lo
   * da el entorno, no el bundle.
   */
  static async getScorecardEstado(): Promise<EstadoScorecard | null> {
    try {
      const response = await fetch(`${API_URL}/dashboard/scorecard/estado`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) return null;
      return (await response.json()) as EstadoScorecard;
    } catch {
      return null;
    }
  }

  /** Scorecard de UNA tienda en una semana (sin `semana`, la última publicada). */
  static async getScorecard(p: {
    semana?: string;
    chain: Chain;
    branchOffice: number;
    semanas?: number;
  }): Promise<ScorecardTienda> {
    const params = new URLSearchParams();
    if (p.semana) params.set('semana', p.semana);
    params.set('chain', p.chain);
    params.set('branchOffice', String(p.branchOffice));
    if (p.semanas) params.set('semanas', String(p.semanas));
    const response = await fetch(`${API_URL}/dashboard/scorecard?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<ScorecardTienda>(response);
  }

  /** Ranking de una semana dentro de la cadena o la regional (general o de un KPI). */
  static async getScorecardRanking(p: {
    semana?: string;
    grupo: 'cadena' | 'regional';
    chain?: Chain;
    regional?: string;
    kpi?: string;
  }): Promise<RankingScorecard> {
    const params = new URLSearchParams();
    if (p.semana) params.set('semana', p.semana);
    params.set('grupo', p.grupo);
    if (p.chain) params.set('chain', p.chain);
    if (p.regional) params.set('regional', p.regional);
    if (p.kpi) params.set('kpi', p.kpi);
    const response = await fetch(`${API_URL}/dashboard/scorecard/ranking?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<RankingScorecard>(response);
  }
}
